/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { List } from '@fluentui/react/lib/List';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { TestPaneEntityPrediction } from 'src/luis/projects/testPane/components/common/TestPaneEntityPrediction';
import { TestPaneSectionLabel } from 'src/luis/projects/testPane/components/common/TestPaneSectionLabel';
import { EndpointUtteranceResult } from 'src/luis/projects/testPane/types/types';

export type PatternInspectorProps = {
    /**
     * The predicted pattern per utterance
     */
    predictedPatterns: EndpointUtteranceResult['predictedPatterns'];
};

const messages = defineMessages({
    topMatchedPattern: {
        id: 'testPane.patternInspector.topMatchedPattern',
        defaultMessage: 'Top-matched patterns'
    },
    noMatchedPatterns: {
        id: 'testPane.patternInspector.noMatchedPatterns',
        defaultMessage: 'No matched patterns'
    }
});

/**
 * Represents the patterns segment in the utterance inspector.
 *
 * @param props The input properties for the inspector.
 */
export const PatternInspector = (props: PatternInspectorProps) => {
    const { predictedPatterns } = props;
    const { localize } = useLocalization();

    const items = predictedPatterns.slice().map(p => ({ key: p.patternName, intentName: p.intentName, patternText: p.patternName }));

    const renderPatterns = () => {
        if (predictedPatterns.length) {
            return (
                <List
                    items={items}
                    onRenderCell={item => <TestPaneEntityPrediction entityName={item.intentName} utteranceSubtext={item.patternText} />}
                />
            );
        }

        return <LuisText data-automation-id="emptyMessage">{localize(messages.noMatchedPatterns)}</LuisText>;
    };

    return (
        <Stack>
            <TestPaneSectionLabel>{localize(messages.topMatchedPattern)}</TestPaneSectionLabel>

            {renderPatterns()}
        </Stack>
    );
};
